import {useEffect, useMemo, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Room as SapiRoom} from '@findhotel/sapi'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {getRoomOfferAnalyticsContexts} from 'middleware/analytics/selectors'
import {
  getFilteredRoomsData,
  getIsRoomsPollingComplete,
  getIsRoomsQueryLoading
} from 'modules/searchApi/selectors'

import {getIsAuthenticated} from '@vio/core/src/auth/modules/selectors'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import {DealFreezeErrorSource} from '@vio/shared/src/dealFreeze/components/ErrorMessage/types'
import {useResumableDealFreeze} from '@vio/shared/src/dealFreeze/hooks/useResumableDealFreeze'
import {useStashedOfferId} from '@vio/shared/src/dealFreeze/hooks/useStashedOfferId'
import {
  getIsDealFreezeEnabled,
  getIsFreezeOverlayOpen,
  getIsResumePending,
  getResumeRoomsErrored
} from '@vio/shared/src/dealFreeze/modules/selector'
import {
  beginDealFreezeResume,
  failDealFreezeResume
} from '@vio/shared/src/dealFreeze/modules/slice'
import {DealFreezeEndpoints} from '@vio/shared/src/dealFreeze/services/dealFreezeApi'
import {
  clearFreezeIntent,
  readFreezeIntent
} from '@vio/shared/src/dealFreeze/utils/freezeIntent'
import {clearDealFreezeStash} from '@vio/shared/src/dealFreeze/utils/stash'
import {DF_FLOW_PARAM} from '@vio/shared/src/dealFreeze/utils/url/constants'

interface Options {
  hotelId: string
  /** Side-effect to run before dispatching the resume (e.g. desktop scrolls to Rooms). */
  onResume?: () => void
}

// SapiRoom endpoint makes the error UI resolve to SoldOut / Unavailability.
const UNAVAILABLE_ERROR = {
  errored: true,
  endpoint: DealFreezeEndpoints.SapiRoom,
  component: DealFreezeErrorSource.FreezeOverlay
}

const OFFERS_ERROR = {
  errored: true,
  component: DealFreezeErrorSource.FreezeOverlay
}

/**
 * Post-auth redirect back to the AP. With a committed freeze intent it opens the
 * overlay in the freezing loader immediately, then populates or fails it once rooms
 * settle. Without one it opens only once offers + rooms are ready.
 * Cast: core Room's optional fields are required in SAPI Room, compatible at runtime.
 */
export const useResumableDealFreezeRedirect = ({
  hotelId,
  onResume
}: Options): void => {
  const dispatch = useDispatch()
  const isHydrated = useIsHydrated()
  const isAuthenticated = useSelector(getIsAuthenticated)
  const isDealFreezeEnabled = useSelector(getIsDealFreezeEnabled)
  const isRoomsLoading = useSelector(getIsRoomsQueryLoading)
  const isRoomsPollingComplete = useSelector(getIsRoomsPollingComplete)
  const isResumePending = useSelector(getIsResumePending)
  const isOverlayOpen = useSelector(getIsFreezeOverlayOpen)
  const roomsErrored = useSelector(state =>
    getResumeRoomsErrored(state, hotelId)
  )
  const {isComplete, isError: isOffersError} = useAccommodationTopOffers({
    hotelId
  })
  const filteredRoomsData = useSelector(getFilteredRoomsData)
  const stashedOfferId = useStashedOfferId()
  const analyticsContextOverride = useSelector(state =>
    stashedOfferId
      ? getRoomOfferAnalyticsContexts(state, stashedOfferId)
      : undefined
  )
  const {
    resumableDealFreezeReady,
    resumeDealFreeze,
    stashedHotel,
    stashedSearchDetail
  } = useResumableDealFreeze({
    rooms: filteredRoomsData?.rooms as unknown as SapiRoom[],
    analyticsContextOverride
  })
  const roomsCount = filteredRoomsData?.rooms?.length ?? 0

  // Snapshotted at hydration: the intent is consumed later by FreezeDetails.
  const hasCommittedIntent = useMemo(
    () => (isHydrated ? Boolean(readFreezeIntent()?.committed) : false),
    [isHydrated]
  )

  // Guards the initial begin dispatch to fire once.
  const hasStartedResumeRef = useRef(false)
  const wasResumePendingRef = useRef(false)

  const failResume = (
    error: typeof UNAVAILABLE_ERROR | typeof OFFERS_ERROR
  ) => {
    clearDealFreezeStash()
    clearFreezeIntent()
    dispatch(failDealFreezeResume(error))
  }

  // No committed intent: open only once offers + rooms are ready, no loader.
  useEffect(() => {
    if (hasCommittedIntent) return
    const isDfFlow = new URLSearchParams(window.location.search).has(
      DF_FLOW_PARAM
    )
    if (
      isDealFreezeEnabled &&
      isAuthenticated &&
      !isRoomsLoading &&
      isComplete &&
      roomsCount > 0 &&
      isDfFlow &&
      resumableDealFreezeReady
    ) {
      onResume?.()
      resumeDealFreeze()
    }
    // resumableDealFreezeReady flips false after the first resume, so it won't re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasCommittedIntent,
    isDealFreezeEnabled,
    isAuthenticated,
    isRoomsLoading,
    isComplete,
    roomsCount,
    resumableDealFreezeReady
  ])

  // Committed intent: open the loader as soon as we land, before offers poll.
  useEffect(() => {
    if (!hasCommittedIntent || hasStartedResumeRef.current) return
    const isDfFlow = new URLSearchParams(window.location.search).has(
      DF_FLOW_PARAM
    )
    if (
      isDealFreezeEnabled &&
      isAuthenticated &&
      isDfFlow &&
      resumableDealFreezeReady
    ) {
      hasStartedResumeRef.current = true
      dispatch(
        beginDealFreezeResume({
          hotel: stashedHotel,
          searchDetail: stashedSearchDetail
        })
      )
    }
    // ref guard keeps this to a single dispatch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasCommittedIntent,
    isDealFreezeEnabled,
    isAuthenticated,
    resumableDealFreezeReady,
    dispatch
  ])

  useEffect(() => {
    if (!isResumePending) return

    // Rooms cache never flips complete on error, so fail here rather than hang.
    if (roomsErrored) {
      failResume(UNAVAILABLE_ERROR)
      return
    }

    if (isOffersError) {
      failResume(OFFERS_ERROR)
      return
    }

    // Wait for both polls so the full offer set is present before matching.
    if (!isComplete || !isRoomsPollingComplete) return

    if (roomsCount > 0) {
      onResume?.()
      const outcome = resumeDealFreeze()

      if (outcome !== 'resumed') failResume(UNAVAILABLE_ERROR)
      return
    }

    // No regular room (empty or split-booking-only) → sold out.
    failResume(UNAVAILABLE_ERROR)

    // isResumePending flips false after the first resolution, so it won't re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isResumePending,
    roomsErrored,
    isOffersError,
    isComplete,
    isRoomsPollingComplete,
    roomsCount,
    dispatch
  ])

  // Loader dismissed (close/back): clear stash + intent so it cannot auto-freeze
  // later. Populate/fail keep the overlay open, so they're excluded.
  useEffect(() => {
    const wasPending = wasResumePendingRef.current
    wasResumePendingRef.current = isResumePending
    if (wasPending && !isResumePending && !isOverlayOpen) {
      clearDealFreezeStash()
      clearFreezeIntent()
    }
  }, [isResumePending, isOverlayOpen])
}
